import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useLocation } from 'react-router-dom';
import { StudySession, studySessionsApi, transcriptionApi, documentsApi } from '../../../services/api';
import { Card, SpaceCard } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Upload, FileText, Play, Mic, X, CheckCircle, AlertCircle, Clock, XCircle, Target, Plus, Minus, Trash2 } from 'lucide-react';
import { FlowStep } from '../NewStudySession';
import { MixedText } from '../../../utils/rtl';

interface UploadStepProps {
  session: StudySession;
  onSessionUpdate: (session: StudySession) => void;
  onStepChange: (step: FlowStep) => void;
  isRTL: boolean;
}

const UploadStep: React.FC<UploadStepProps> = ({
  session,
  onSessionUpdate,
  onStepChange,
  isRTL
}) => {
  const { t } = useTranslation();
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);
  const [docUploadProgress, setDocUploadProgress] = useState(0);
  const [uploadingAudioFile, setUploadingAudioFile] = useState<string | null>(null);
  const [uploadingDocFile, setUploadingDocFile] = useState<string | null>(null);
  const [learningFocus, setLearningFocus] = useState(session?.learning_focus || '');
  const [isLearningFocusExpanded, setIsLearningFocusExpanded] = useState(false);
  const [deletingTranscription, setDeletingTranscription] = useState<number | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<number | null>(null);
  const transcriptionCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Helper function to check for processing transcriptions
  const hasProcessingTranscription = () => Boolean(session?.transcriptions?.some(t => t.status === 'processing' || t.status === 'pending'));

  // Check if we're in a critical state
  const isInCriticalState = useCallback(() => {
    return uploadingAudio || uploadingDoc;
  }, [uploadingAudio, uploadingDoc, session?.transcriptions]);

  // Browser beforeunload event for browser navigation/closing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isInCriticalState()) {
        e.preventDefault();
        e.returnValue = t('sessions:flow.upload.exitConfirmation', 'You have uploads in progress. Are you sure you want to leave? Your uploads will be lost.');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isInCriticalState, t]);

  // Custom navigation blocking for in-app navigation
  const handleNavigation = useCallback((path: string) => {
    if (isInCriticalState()) {
      setPendingNavigation(path);
      setShowExitConfirm(true);
    } else {
      navigate(path);
    }
  }, [navigate, isInCriticalState]);

  // Handle confirmation dialog
  const handleExitConfirm = useCallback((confirmed: boolean) => {
    setShowExitConfirm(false);
    if (confirmed && pendingNavigation) {
      navigate(pendingNavigation);
    }
    setPendingNavigation(null);
  }, [navigate, pendingNavigation]);

  // Periodic transcription status checking
  useEffect(() => {
    const checkTranscriptionStatus = async () => {
      if (!session?.transcriptions?.length) return;

      console.log('Session transcriptions:', session.transcriptions);

      const processingTranscriptions = session.transcriptions.filter(
        t => t.status === 'processing' || t.status === 'pending'
      );

      if (processingTranscriptions.length === 0) {
        return;
      }

      try {
        // Check each processing transcription
        for (const transcription of processingTranscriptions) {
          await transcriptionApi.getTranscriptionStatus(transcription.id);
        }
        
        // Refresh the entire session to get updated transcription statuses
        const updatedSession = await studySessionsApi.getSession(session.id);
        onSessionUpdate(updatedSession);
      } catch (error) {
        console.error('Error checking transcription status:', error);
      }
    };

    // Clear any existing interval first
    if (transcriptionCheckInterval.current) {
      clearInterval(transcriptionCheckInterval.current);
      transcriptionCheckInterval.current = null;
    }

    // Set up interval if there are processing transcriptions
    const processingTranscriptions = session?.transcriptions?.filter(
      t => t.status === 'processing' || t.status === 'pending'
    );

    if (processingTranscriptions?.length > 0) {
      // Check immediately
      checkTranscriptionStatus();
      
      // Set up periodic checking every 20 seconds
      transcriptionCheckInterval.current = setInterval(checkTranscriptionStatus, 20000);
    }

    // Cleanup interval on component unmount or when session changes
    return () => {
      if (transcriptionCheckInterval.current) {
        clearInterval(transcriptionCheckInterval.current);
        transcriptionCheckInterval.current = null;
      }
    };
  }, [session?.id, session?.transcriptions?.length]); // Depend on both session.id and transcriptions count

  const handleAudioUpload = async (files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    setUploadingAudio(true);
    setUploadingAudioFile(file.name);
    setAudioUploadProgress(0);

    try {
      const response = await transcriptionApi.uploadAudio(
        session.id, 
        file,
        (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setAudioUploadProgress(percentCompleted);
          }
        }
      );

      // Use the response to update session directly instead of refetching
      if (response) {
        const updatedSession = {
          ...session,
          transcriptions: [
            ...(session.transcriptions || []),
            response
          ]
        };
        onSessionUpdate(updatedSession);
      } else {
        // Fallback to refetching if response doesn't contain expected data
        const updatedSession = await studySessionsApi.getSession(session.id);
        onSessionUpdate(updatedSession);
      }
      
      setAudioUploadProgress(100);
      setTimeout(() => {
        setUploadingAudio(false);
        setUploadingAudioFile(null);
        setAudioUploadProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Audio upload error:', error);
      setUploadingAudio(false);
      setUploadingAudioFile(null);
      setAudioUploadProgress(0);
    }
  };

  const handleDocumentUpload = async (files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    setUploadingDoc(true);
    setUploadingDocFile(file.name);
    setDocUploadProgress(0);

    try {
      const response = await documentsApi.uploadDocument(
        session.id, 
        file,
        (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDocUploadProgress(percentCompleted);
          }
        }
      );
      
      // Use the response to update session directly instead of refetching
      if (response) {
        const updatedSession = {
          ...session,
          documents: [
            ...(session.documents || []),
            response
          ]
        };
        onSessionUpdate(updatedSession);
      } else {
        // Fallback to refetching if response doesn't contain expected data
        const updatedSession = await studySessionsApi.getSession(session.id);
        onSessionUpdate(updatedSession);
      }
      
      setDocUploadProgress(100);
      setTimeout(() => {
        setUploadingDoc(false);
        setUploadingDocFile(null);
        setDocUploadProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Document upload error:', error);
      setUploadingDoc(false);
      setUploadingDocFile(null);
      setDocUploadProgress(0);
    }
  };

  const handleDeleteTranscription = async (transcriptionId: number) => {
    if (!session) return;
    
    setDeletingTranscription(transcriptionId);
    
    try {
      await transcriptionApi.deleteTranscription(transcriptionId);
      
      // Refresh session to get updated data
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error deleting transcription:', error);
    } finally {
      setDeletingTranscription(null);
    }
  };

  const handleCancelTranscription = async (transcriptionId: number) => {
    if (!session) return;
    
    setDeletingTranscription(transcriptionId);
    
    try {
      await transcriptionApi.cancelTranscription(transcriptionId);
      
      // Refresh session to get updated data
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error canceling transcription:', error);
    } finally {
      setDeletingTranscription(null);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!session) return;
    
    setDeletingDocument(documentId);
    
    try {
      await documentsApi.deleteDocument(documentId);
      
      // Refresh session to get updated data
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeletingDocument(null);
    }
  };

  const audioDropzone = useDropzone({
    onDrop: handleAudioUpload,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.ogg'],
      'video/*': ['.mp4', '.avi', '.mov', '.mkv']
    },
    maxFiles: 1,
    disabled: uploadingAudio
  });

  const documentDropzone = useDropzone({
    onDrop: handleDocumentUpload,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt']
    },
    maxFiles: 1,
    disabled: uploadingDoc
  });

  const hasAudioUpload = () => Boolean(session?.transcriptions && session.transcriptions.length > 0);
  const hasDocuments = () => Boolean(session?.documents && session.documents.length > 0);
  const hasAnyContent = () => hasAudioUpload() || hasDocuments();
  const hasCompletedTranscription = () => Boolean(session?.transcriptions?.some(t => t.status === 'completed'));

  const handleContinue = () => {
    if (hasAnyContent()) {
      onStepChange('materials');
    }
  };

  const handleSaveLearningFocus = async () => {
    if (!session) return;
    
    try {
      const updatedSession = await studySessionsApi.updateSession(session.id, {
        learning_focus: learningFocus.trim() || undefined
      });
      
      onSessionUpdate(updatedSession);
    } catch (err: any) {
      console.error('Failed to update learning focus:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    const iconClasses = `w-4 h-4 flex-shrink-0`;
    
    switch (status) {
      case 'completed':
        return <CheckCircle className={`${iconClasses} text-green-600`} />;
      case 'processing':
        return <div className={`${iconClasses} animate-spin border-2 border-vibrant-orange border-t-transparent rounded-full`} />;
      case 'failed':
        return <XCircle className={`${iconClasses} text-vibrant-orange`} />;
      case 'pending':
        return <Clock className={`${iconClasses} text-vibrant-orange`} />;
      default:
        return <AlertCircle className={`${iconClasses} text-surface-muted`} />;
    }
  };

  const renderProgressBar = () => {
    const progress = uploadingAudio ? audioUploadProgress : uploadingDoc ? docUploadProgress : 0;
    return (
      <div className="w-full bg-surface-muted/30 rounded-full h-3 overflow-hidden">
        <div 
          className="bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 h-3 rounded-full transition-all duration-500"
          style={{ 
            width: `${progress}%`
          }}
        />
      </div>
    );
  };

  const renderUploadProgressInfo = () => {
    const progress = uploadingAudio ? audioUploadProgress : uploadingDoc ? docUploadProgress : 0;
    const fileName = uploadingAudio ? uploadingAudioFile : uploadingDoc ? uploadingDocFile : '';
    
    return (
      <div className="space-y-4">
        <div className="w-12 h-12 bg-vibrant-orange/20 rounded-full flex items-center justify-center mx-auto">
          {uploadingAudio ? (
            <Mic className="w-6 h-6 text-vibrant-orange animate-pulse" />
          ) : (
            <FileText className="w-6 h-6 text-vibrant-orange animate-pulse" />
          )}
        </div>
        <div>
          <p className="text-surface-primary font-medium">
            <MixedText>{t('sessions:flow.upload.uploading', 'Uploading...')}</MixedText>
          </p>
          <p className="text-surface-muted text-sm mt-1">{fileName}</p>
          <div className={`text-xs text-surface-muted mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
            {progress}% {t('sessions:flow.upload.complete', 'complete')}
          </div>
        </div>
        {renderProgressBar()}
      </div>
    );
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-bg-primary mb-3 md:mb-4">
          {t('sessions:flow.upload.title', 'Upload Your Study Content')}
        </h2>
        <p className="text-bg-secondary max-w-2xl mx-auto text-sm md:text-base">
          <MixedText>{t('sessions:flow.upload.description', 'Upload audio/video recordings or documents to begin your study session')}</MixedText>
        </p>
      </div>
      
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}>
        {/* Audio Upload */}
        <SpaceCard className={`${isRTL ? 'lg:col-start-2' : ''}`}>
          <div className="p-6">
            <h3 className={`text-lg font-semibold text-surface-primary mb-4 flex items-center ${isRTL ? 'flex-row-reverse text-right' : 'space-x-2'}`}>
              <Play className="w-5 h-5 text-vibrant-orange" />
              <span className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                {t('sessions:flow.upload.audioVideo', 'Audio/Video')}
              </span>
            </h3>
            
            <div
              {...audioDropzone.getRootProps()}
              className={`
                border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
                ${audioDropzone.isDragActive 
                  ? 'border-vibrant-orange bg-vibrant-orange/10' 
                  : 'border-surface-muted/50 hover:border-vibrant-orange/50 hover:bg-vibrant-orange/5'
                }
                ${uploadingAudio ? 'pointer-events-none opacity-50' : ''}
              `}
            >
              <input {...audioDropzone.getInputProps()} />
              
              {uploadingAudio ? (
                renderUploadProgressInfo()
              ) : (
                <div className="space-y-4">
                  <div className="w-12 h-12 bg-vibrant-orange/20 rounded-full flex items-center justify-center mx-auto">
                    <Upload className="w-6 h-6 text-vibrant-orange" />
                  </div>
                  <div>
                    <p className="text-surface-primary font-medium">
                     <MixedText>{t('sessions:flow.upload.dropLecture', 'Drag & drop audio/video files here')}</MixedText>
                    </p>
                    <p className="text-surface-muted text-sm mt-1">
                      <MixedText>{t('sessions:flow.upload.supportedFormats', 'MP3, WAV, M4A, MP4 (max 1GB)')}</MixedText>
                    </p>
                  </div>
                  
                </div>
              )}
            </div>

            {/* Audio Files List */}
            {hasAudioUpload() && (
              <div className="mt-4 md:mt-6 space-y-2 md:space-y-3">
                {session.transcriptions?.map((transcription) => (
                  <div key={transcription.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-surface-muted/10 rounded-lg border border-surface-muted/20 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center flex-1 min-w-0 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                      {getStatusIcon(transcription.status)}
                      <div className={`flex-1 min-w-0 ${isRTL ? 'text-right pr-2' : ''}`}>
                        <p className={`text-sm font-medium text-surface-primary ${isRTL ? 'text-right' : ''} truncate`} title={transcription.original_filename || 'Audio File'}>
                          {transcription.original_filename || 'Audio File'}
                        </p>
                        <p className={`text-xs text-surface-muted ${isRTL ? 'text-right' : ''} truncate`}>
                          <MixedText>{transcription.status === 'completed' ? t('sessions:flow.upload.transcriptionComplete') : 
                           transcription.status === 'processing' ? t('sessions:flow.transcriptionTiming.processingMessage') : 
                           transcription.status === 'failed' ? t('sessions:flow.upload.transcriptionFailed') : 
                           transcription.status === 'pending' ? t('sessions:flow.transcriptionTiming.pendingMessage') : 
                           t('sessions:flow.upload.unknownStatus')}
                          </MixedText>
                        </p>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'space-x-reverse' : ''} ${isRTL ? 'self-end sm:self-center' : 'self-end sm:self-center'}`}>
                      {(transcription.status === 'processing' || transcription.status === 'pending') && (
                        <button
                          onClick={() => handleCancelTranscription(transcription.id)}
                          disabled={deletingTranscription === transcription.id}
                          className={`p-2 rounded-lg transition-colors ${
                            deletingTranscription === transcription.id
                              ? 'bg-surface-muted/20 text-surface-muted cursor-not-allowed'
                              : 'bg-surface-muted/20 hover:bg-vibrant-orange/20 text-surface-muted hover:text-vibrant-orange'
                          }`}
                          title={t('sessions:flow.upload.cancelTranscription', 'Cancel transcription')}
                        >
                          {deletingTranscription === transcription.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-surface-muted border-t-transparent" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      
                      {(transcription.status === 'failed' || transcription.status === 'completed') && (
                        <button
                          onClick={() => handleDeleteTranscription(transcription.id)}
                          disabled={deletingTranscription === transcription.id}
                          className={`p-2 rounded-lg transition-colors ${
                            deletingTranscription === transcription.id
                              ? 'bg-surface-muted/20 text-surface-muted cursor-not-allowed'
                              : 'bg-surface-muted/20 hover:bg-red-500/20 text-surface-muted hover:text-red-500'
                          }`}
                          title={t('sessions:flow.upload.deleteTranscription', 'Delete transcription')}
                        >
                          {deletingTranscription === transcription.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-surface-muted border-t-transparent" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SpaceCard>

        {/* Document Upload */}
        <SpaceCard className={`${isRTL ? 'lg:col-start-1' : ''}`}>
          <div className="p-6">
            <h3 className={`text-lg font-semibold text-surface-primary mb-4 flex items-center ${isRTL ? 'flex-row-reverse text-right' : 'space-x-2'}`}>
              <FileText className="w-5 h-5 text-vibrant-orange" />
              <span className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                {t('sessions:flow.upload.supportingDocuments', 'Documents')}
              </span>
            </h3>
            
            <div
              {...documentDropzone.getRootProps()}
              className={`
                border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
                ${documentDropzone.isDragActive 
                  ? 'border-vibrant-orange bg-vibrant-orange/10' 
                  : 'border-surface-muted/50 hover:border-vibrant-orange/50 hover:bg-vibrant-orange/5'
                }
                ${uploadingDoc ? 'pointer-events-none opacity-50' : ''}
              `}
            >
              <input {...documentDropzone.getInputProps()} />
              
              {uploadingDoc ? (
                renderUploadProgressInfo()
              ) : (
                <div className="space-y-4">
                  <div className="w-12 h-12 bg-vibrant-orange/20 rounded-full flex items-center justify-center mx-auto">
                    <Upload className="w-6 h-6 text-vibrant-orange" />
                  </div>
                  <div>
                    <p className="text-surface-primary font-medium">
                      {t('sessions:flow.upload.dropDocuments', 'Drag & drop documents here')}
                    </p>
                    <p className="text-surface-muted text-sm mt-1">
                      {t('sessions:flow.upload.documentFormats', 'PDF, DOCX, DOC, TXT')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Document Files List */}
            {hasDocuments() && (
              <div className="mt-4 md:mt-6 space-y-2 md:space-y-3">
                {session.documents?.map((document) => (
                  <div key={document.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-surface-muted/10 rounded-lg border border-surface-muted/20 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                    <div className={`flex items-center flex-1 min-w-0 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'}`}>
                        <FileText className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                      </div>
                      <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-2' : 'order-2'}`}>
                        <p className={`text-sm font-medium text-surface-primary ${isRTL ? 'text-right' : ''} truncate`} title={document.original_filename || 'Document'}>
                          {document.original_filename || 'Document'}
                        </p>
                        <p className={`text-xs text-surface-muted ${isRTL ? 'text-right' : ''} truncate`} title={document.file_type}>
                          {document.file_type}
                        </p>
                      </div>
                    </div>
                    
                    {/* Delete button */}
                    <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'space-x-reverse' : ''} ${isRTL ? 'self-end sm:self-center' : 'self-end sm:self-center'}`}>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        disabled={deletingDocument === document.id}
                        className={`p-2 rounded-lg transition-colors ${
                          deletingDocument === document.id
                            ? 'bg-surface-muted/20 text-surface-muted cursor-not-allowed'
                            : 'bg-surface-muted/20 hover:bg-red-500/20 text-surface-muted hover:text-red-500'
                        }`}
                        title={t('sessions:flow.upload.deleteDocument', 'Delete document')}
                      >
                        {deletingDocument === document.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-surface-muted border-t-transparent" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SpaceCard>
      </div>

      {/* Learning Focus Card - Optional */}
      <div className="w-full">
        <SpaceCard className={`transition-all duration-300 ${isLearningFocusExpanded ? 'border-vibrant-orange/50 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/5 shadow-lg shadow-vibrant-orange/20' : 'border-surface-muted/30 hover:border-vibrant-orange/50'}`}>
          <div 
            className={`flex items-center justify-between p-4 cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
            onClick={() => setIsLearningFocusExpanded(!isLearningFocusExpanded)}
          >
            <div className={`flex items-center flex-1 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'} ${isLearningFocusExpanded ? 'bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 text-white shadow-lg' : 'bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 text-white'}`}>
                <Target className="w-6 h-6" />
              </div>
              <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-4' : 'order-2 text-left pl-4'}`}>
                <h3 className={`font-semibold text-base ${isLearningFocusExpanded ? 'text-vibrant-orange' : 'text-surface-primary'} ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? (
                    <MixedText>{t('sessions:flow.upload.learningFocus', 'מיקוד למידה (אופציונלי)')}</MixedText>
                  ) : (
                    t('sessions:flow.upload.learningFocus', 'Learning Focus (Optional)')
                  )}
                </h3>
              </div>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
              {learningFocus && (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              )}
              {isLearningFocusExpanded ? (
                <Minus className="w-5 h-5 text-surface-muted" />
              ) : (
                <Plus className="w-5 h-5 text-surface-muted" />
              )}
            </div>
          </div>
          
          {isLearningFocusExpanded && (
            <div className="px-4 pb-4 border-t border-surface-muted/30 pt-4">
              <div className="space-y-3">
                <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                  <label className={`block text-sm font-medium text-surface-primary mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {isRTL ? (
                      <MixedText>{t('sessions:flow.upload.whatToFocus', 'על מה תרצה להתמקד?')}</MixedText>
                    ) : (
                      t('sessions:flow.upload.whatToFocus', 'What would you like to focus on?')
                    )}
                  </label>
                  <textarea
                    value={learningFocus}
                    onChange={(e) => setLearningFocus(e.target.value)}
                    onBlur={handleSaveLearningFocus}
                    placeholder={isRTL ? 
                      'לדוגמה: מושגי יסוד, דוגמאות מעשיות, הכנה לבחינה...' : 
                      'e.g., Key concepts, practical examples, exam preparation...'
                    }
                    className={`w-full px-3 py-2 border border-surface-muted/30 rounded-lg bg-white/50 dark:bg-deep-space-purple/20 text-surface-primary placeholder-surface-muted resize-none focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                    rows={3}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                
                <div className={`text-xs text-surface-muted ${isRTL ? 'text-right' : 'text-left'}`}>
                  {isRTL ? (
                    <MixedText>{t('sessions:flow.upload.focusHint', 'המיקוד שלך יעזור ל-AI לייצר חומרים רלוונטיים יותר')}</MixedText>
                  ) : (
                    t('sessions:flow.upload.focusHint', 'Your focus will help the AI generate more relevant materials')
                  )}
                </div>
              </div>
            </div>
          )}
        </SpaceCard>
      </div>

      {/* Navigation */}
      <div className="flex justify-center pt-8">
        {uploadingAudio || uploadingDoc ? (
          <div className={`bg-vibrant-orange/10 border border-vibrant-orange/30 rounded-lg px-6 py-4 flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-vibrant-orange border-t-transparent mr-3 ml-3"></div>
            <span className="text-vibrant-orange font-medium"><MixedText>{t('sessions:flow.upload.uploading')}</MixedText></span>
          </div>
        ) : hasProcessingTranscription() ? (
          <div className="bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/5 border border-vibrant-orange/30 rounded-lg px-6 py-4 shadow-lg shadow-vibrant-orange/20">
            <div className={`flex items-center mb-2 ${isRTL ? 'justify-end space-x-reverse space-x-3' : 'space-x-3'}`}>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-vibrant-orange border-t-transparent mr-3 ml-3"></div>
              <span className="text-vibrant-orange font-medium"><MixedText>{t('sessions:flow.upload.processingLecture')}</MixedText></span>
            </div>
            <p className={`text-sm text-vibrant-orange/80 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('sessions:flow.transcriptionTiming.patience')}
            </p>
          </div>
        ) : hasAnyContent() && (hasCompletedTranscription() || !hasAudioUpload()) ? (
          <Button
            onClick={handleContinue}
            variant="primary"
            size="lg"
            className="px-8"
          >
            {t('sessions:flow.upload.continueToMaterials')}
          </Button>
        ) : hasAudioUpload() ? (
          <div className="bg-white/80 dark:bg-deep-space-purple/20 backdrop-blur-sm border border-surface-muted/30 rounded-lg px-6 py-4 shadow-sm">
            <p className="text-surface-primary font-medium text-center">⏳ {t('sessions:flow.upload.waitingTranscription')}</p>
          </div>
        ) : (
          <div className="bg-surface-muted/10 border border-surface-muted/30 rounded-lg px-6 py-4">
            <p className="text-surface-muted text-center">{t('sessions:flow.upload.uploadRequired')}</p>
          </div>
        )}
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-deep-space-purple rounded-2xl p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-xl font-bold text-surface-primary mb-4">
              ⚠️ {t('common:confirm.title', 'Confirm Navigation')}
            </h3>
            <p className="text-surface-secondary mb-6">
              {uploadingAudio || uploadingDoc
                ? t('sessions:flow.upload.uploadInProgressWarning', 'You have an upload in progress. Leaving now will cancel the upload and you will lose your file. Are you sure you want to leave?')
                : t('sessions:flow.upload.processingInProgressWarning', 'Your audio is being transcribed. Leaving now may interrupt the process. Are you sure you want to leave?')}
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => handleExitConfirm(false)}
                variant="secondary"
                size="md"
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={() => handleExitConfirm(true)}
                variant="primary"
                size="md"
                className="bg-vibrant-orange hover:bg-vibrant-orange/90"
              >
                {t('common:buttons.leave', 'Leave Anyway')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStep; 